import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import FilePreview from "../../../components/MessageHistory/FilePreview";
import { pickLocalFiles, TASK_COMMENT_FILE_ACCEPT } from "../../../utils/pickLocalFiles";
import {
  applyPickedMention,
  buildComposerMentionsPayload,
  buildMentionCatalog,
  buildTeamMentionOptions,
  emptyTeamMentions,
  findActiveMentionAt,
} from "../../Team/utils/teamMentions";

const MENTION_OVERLAY_Z_INDEX = 10000;
export const TASK_COMMENT_DROP_ZONE_SELECTOR = "[data-task-comment-drop-zone]";

function renderCommentBody(text, theme) {
  const raw = String(text || "");
  if (!raw) return null;

  const parts = raw.split(/(@[^\s]+)/g);
  return parts.map((part, idx) => {
    if (/^@[^\s]+$/.test(part)) {
      return (
        <Box
          key={`m-${idx}`}
          component="span"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
          }}
        >
          {part}
        </Box>
      );
    }
    return <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>;
  });
}

export { renderCommentBody };

const TaskCommentComposer = ({
  value,
  onChange,
  onSubmit,
  submitting = false,
  disabled = false,
  users = [],
  boardMembers = [],
  board = null,
  currentUserId = null,
  placeholder,
  submitLabel,
  submitHint,
  minRows = 2,
}) => {
  const { t } = useTranslation("teamTasks");
  const { t: tTeam } = useTranslation("team");
  const theme = useTheme();
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [pickedMentions, setPickedMentions] = useState(emptyTeamMentions);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [mentionOverlayPosition, setMentionOverlayPosition] = useState(null);
  const [cursor, setCursor] = useState(0);
  const [files, setFiles] = useState([]);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const fileDragDepthRef = useRef(0);

  const restrictToMembers = Boolean(board && !board.spaceId && !board.leadId);

  const mentionCatalog = useMemo(
    () =>
      buildMentionCatalog({
        members: boardMembers,
        globalUsers: users,
        globalAgents: [],
        currentUserId,
        restrictToMembers,
      }),
    [boardMembers, users, currentUserId, restrictToMembers]
  );

  const activeMention = useMemo(
    () => findActiveMentionAt(value, cursor),
    [value, cursor]
  );

  const mentionOptions = useMemo(
    () =>
      buildTeamMentionOptions({
        users: mentionCatalog.users,
        agents: [],
        query: activeMention?.query ?? "",
        labels: {
          allUsers: tTeam("mentionAllUsers"),
          human: tTeam("mentionHuman"),
        },
      }),
    [mentionCatalog.users, activeMention?.query, tTeam]
  );

  const mentionPickerOpen = Boolean(activeMention) && mentionOptions.length > 0;

  useEffect(() => {
    setMentionHighlightIndex(0);
  }, [mentionOptions.length, activeMention?.query]);

  useLayoutEffect(() => {
    if (mentionPickerOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setMentionOverlayPosition({
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: Math.max(rect.width, 280),
      });
    } else {
      setMentionOverlayPosition(null);
    }
  }, [mentionPickerOpen, mentionOptions.length, value]);

  const handleSelectMention = (option) => {
    if (!activeMention) return;
    const before = value.slice(0, activeMention.atIndex);
    const after = value.slice(cursor);
    const label = option.label || "";
    const insert = `@${label} `;
    const next = `${before}${insert}${after}`;
    onChange(next);
    setPickedMentions((prev) => applyPickedMention(prev, option));
    const nextCursor = before.length + insert.length;
    setCursor(nextCursor);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      }
    });
  };

  const buildMentionsPayload = () =>
    buildComposerMentionsPayload(value, pickedMentions, {
      users: mentionCatalog.parseUsers,
      agents: mentionCatalog.parseAgents,
    });

  const addFiles = useCallback((fileOrFiles) => {
    if (!fileOrFiles || submitting || disabled) return;
    const toAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    const incoming = toAdd.filter(Boolean);
    if (!incoming.length) return;
    setFiles((prev) => [...prev, ...incoming]);
  }, [submitting, disabled]);

  const handleAttachClick = () => {
    if (submitting || disabled) return;
    void pickLocalFiles({ multiple: true, accept: TASK_COMMENT_FILE_ACCEPT }).then((selected) => {
      if (selected.length) addFiles(selected);
    });
  };

  const isFileDrag = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    return types.includes("Files") || types.includes("files");
  };

  const resetFileDragOver = () => {
    fileDragDepthRef.current = 0;
    setIsFileDragOver(false);
  };

  const handleFileDragEnter = (e) => {
    if (!isFileDrag(e) || submitting || disabled) return;
    e.preventDefault();
    fileDragDepthRef.current += 1;
    if (fileDragDepthRef.current === 1) setIsFileDragOver(true);
  };

  const handleFileDragOver = (e) => {
    if (!isFileDrag(e) || submitting || disabled) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const handleFileDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
    if (fileDragDepthRef.current === 0) setIsFileDragOver(false);
  };

  const handleDropFiles = (e) => {
    if (!isFileDrag(e) || submitting || disabled) {
      resetFileDragOver();
      return;
    }
    const dropped = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    e.preventDefault();
    e.stopPropagation();
    resetFileDragOver();
    if (dropped.length) addFiles(dropped);
  };

  const handlePasteFiles = (e) => {
    const clipboard = e.clipboardData;
    if (!clipboard) return;
    const pasted = clipboard.files?.length
      ? Array.from(clipboard.files)
      : Array.from(clipboard.items || [])
          .filter((item) => item.kind === "file")
          .map((item) => item.getAsFile())
          .filter(Boolean);
    if (!pasted.length) return;
    e.preventDefault();
    e.stopPropagation();
    addFiles(pasted);
  };

  const canSubmit = Boolean(value.trim() || files.length) && !submitting && !disabled;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const result = await Promise.resolve(
      onSubmit({
        body: value.trim(),
        mentions: buildMentionsPayload(),
        files,
      })
    );
    if (result === null || result === false) return;
    setPickedMentions(emptyTeamMentions());
    setFiles([]);
  };

  const handleKeyDown = (e) => {
    if (mentionPickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHighlightIndex((i) => (i + 1) % mentionOptions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHighlightIndex(
          (i) => (i - 1 + mentionOptions.length) % mentionOptions.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const opt = mentionOptions[mentionHighlightIndex];
        if (opt) handleSelectMention(opt);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        return;
      }
    }

    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      ref={anchorRef}
      data-task-comment-drop-zone=""
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleDropFiles}
      sx={{
        borderRadius: 1.5,
        outline: isFileDragOver ? `2px dashed ${theme.palette.primary.main}` : "none",
        outlineOffset: 4,
        bgcolor: isFileDragOver
          ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.08)
          : "transparent",
        transition: "background-color 0.15s ease, outline-color 0.15s ease",
      }}
    >
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setCursor(e.target.selectionStart ?? e.target.value.length);
        }}
        onClick={(e) => setCursor(e.target.selectionStart ?? value.length)}
        onKeyUp={(e) => setCursor(e.target.selectionStart ?? value.length)}
        onSelect={(e) => setCursor(e.target.selectionStart ?? value.length)}
        placeholder={placeholder || t("commentPlaceholder")}
        fullWidth
        multiline
        minRows={minRows}
        maxRows={8}
        size="small"
        disabled={submitting || disabled}
        onKeyDown={handleKeyDown}
        onPaste={handlePasteFiles}
      />

      {mentionOverlayPosition && mentionPickerOpen
        ? createPortal(
            <Box
              role="listbox"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                position: "fixed",
                bottom: mentionOverlayPosition.bottom,
                left: mentionOverlayPosition.left,
                width: mentionOverlayPosition.width,
                maxHeight: "min(280px, 40vh)",
                overflow: "auto",
                bgcolor: "background.paper",
                boxShadow: theme.shadows[8],
                borderRadius: 1.5,
                zIndex: MENTION_OVERLAY_Z_INDEX,
                border: 1,
                borderColor: "divider",
              }}
            >
              {mentionOptions.map((opt, idx) => (
                <Box
                  key={`${opt.type}-${opt.id}-${idx}`}
                  component="div"
                  role="option"
                  aria-selected={idx === mentionHighlightIndex}
                  onClick={() => handleSelectMention(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor:
                      idx === mentionHighlightIndex ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="body2" fontWeight={opt.type === "all" ? 700 : 500} noWrap>
                    @{opt.label}
                  </Typography>
                  {opt.kind ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {opt.kind}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>,
            document.body
          )
        : null}

      {files.length > 0 ? (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {files.map((file, index) => (
            <FilePreview
              key={`${file.name}-${file.size}-${index}`}
              file={file}
              onRemove={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
            />
          ))}
        </Box>
      ) : null}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }} spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Tooltip title={t("commentAttachFile")}>
            <span>
              <IconButton
                size="small"
                onClick={handleAttachClick}
                disabled={submitting || disabled}
                aria-label={t("commentAttachAria")}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" noWrap>
            {isFileDragOver
              ? t("commentDropActive")
              : `${submitHint || t("commentSubmitHint")} · ${t("commentMentionHint")}`}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ fontWeight: 700, borderRadius: 2, flexShrink: 0 }}
        >
          {submitting ? t("savingComment") : submitLabel || t("commentSubmit")}
        </Button>
      </Stack>
    </Box>
  );
};

export default TaskCommentComposer;
